import { createContext, useContext, useLayoutEffect } from 'react';

/**
 * The page's SongShake FX controller (`useFxStage`, ENG-804), or null where
 * no stage draws (outside the stream page's queue, e.g. the boost pick
 * strip). Components read it with `useFx()` and must treat null, and any
 * `status` other than 'ready', as "no effects".
 */
export const FxContext = createContext(null);

/** The FX controller from the nearest `FxContext`, or null. */
export const useFx = () => useContext(FxContext);

/**
 * Registers one queue card with the page's stage while it is mounted, so
 * effects can be played on it (`useFx().getCard(id)`):
 * - `elRef`: the card element (the engine sets its `z-index` while lit or moving),
 * - `innerRef`: the wrapper the engine moves (`transform`: lift, grow, tremble, lean),
 * - `coverSelector`: inside the inner wrapper, the box effects wrap (the engine
 *   sets its `filter` to dim it). Defaults to the inner wrapper itself.
 * The cover is also the `art` for the art-copy streak when it is an <img>.
 * `artKey` re-registers the card when the cover element changes (artwork
 * arriving swaps a placeholder for an <img>).
 * Does nothing without an FxContext or with a null id.
 */
export const useFxCard = (id, { elRef, innerRef, coverSelector = null, artKey = null }) => {
  const fx = useFx();
  const registerCard = fx?.registerCard;
  useLayoutEffect(() => {
    const el = elRef.current;
    const inner = innerRef.current;
    if (!registerCard || id == null || !el || !inner) return undefined;
    const cover = (coverSelector && inner.querySelector(coverSelector)) || inner;
    const art = cover.tagName === 'IMG' ? cover : null;
    return registerCard(id, { el, inner, cover, art });
  }, [registerCard, id, elRef, innerRef, coverSelector, artKey]);
};
